import { useState, useEffect } from 'react';
import { Button, Input, notification, InputNumber, Select } from 'antd';
import { PhoneOutlined, SafetyOutlined, WhatsAppOutlined, UserAddOutlined, LoginOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { baseUrl, rozerpay_key_id } from '../config';

const M  = '#7a0000';
const G  = '#FFD700';

const PORTAL_RELATION_MAP = {
  'सुपुत्र श्री':  'Son of Mr.',
  'सुपुत्री श्री': 'Daughter of Mr.',
  'पत्नी श्री':    'Wife of Mr.',
};

function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function UserPortalLogin() {
  const [tab,     setTab]     = useState('login');   // 'login' | 'register'
  const history = useHistory();

  // ── Login state ──────────────────────────────────────────────────────────
  const [step,     setStep]     = useState('phone');  // 'phone' | 'otp' | 'select'
  const [phone,    setPhone]    = useState('');
  const [otp,      setOtp]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const [accounts, setAccounts] = useState([]);      // multiple accounts on same phone

  // ── Registration state ───────────────────────────────────────────────────
  const [regStep,       setRegStep]       = useState('form'); // 'form' | 'paying' | 'success'
  const [regData,       setRegData]       = useState({ name: '', father_name: '', relation: '', phone: '', village: '', district: '', announced_amount: '' });
  const [regMonths,     setRegMonths]     = useState(1);
  const [regLoading,    setRegLoading]    = useState(false);
  const [joinMission21k, setJoinMission21k] = useState(false); // Mission 21K opt-in during registration
  const [mission21kEnabled, setMission21kEnabled] = useState(true);
  // Parent member selection
  const [hasParentMember, setHasParentMember] = useState(false);
  const [parentSearch,    setParentSearch]    = useState('');
  const [parentResults,   setParentResults]   = useState([]);
  const [selectedParent,  setSelectedParent]  = useState(null); // { id, name, village, membership_no }

  // Auto-redirect if already logged in with a valid token
  useEffect(() => {
    const existingToken = localStorage.getItem('memberToken');
    if (existingToken) {
      axios.get(`${baseUrl}/member/me`, { headers: { Authorization: `Bearer ${existingToken}` } })
        .then(r => { if (r.data.status) history.push('/user-portal'); })
        .catch(() => { localStorage.removeItem('memberToken'); localStorage.removeItem('memberInfo'); });
    }
  }, [history]);

  useEffect(() => {
    axios.get(`${baseUrl}/member/portal-config`)
      .then(r => {
        if (r.data.status && r.data.mission_21k_visible === false) {
          setMission21kEnabled(false);
          setJoinMission21k(false);
        }
      })
      .catch(() => {});
  }, []);

  // ── Login handlers ────────────────────────────────────────────────────────
  const sendOtp = async () => {
    if (!/^\d{10}$/.test(phone) || !/^[6-9]/.test(phone)) {
      notification.warning({ message: 'Valid 10-digit mobile number दर्ज करें।', placement: 'topRight' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/member/send-otp`, { phone });
      if (res.data.status) {
        notification.success({ message: 'OTP WhatsApp पर भेजा गया।', placement: 'topRight' });
        setStep('otp');
      } else {
        notification.error({ message: res.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'OTP भेजने में समस्या हुई।', placement: 'topRight' });
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      notification.warning({ message: '6-digit OTP दर्ज करें।', placement: 'topRight' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/member/verify-otp`, { phone, otp });
      if (res.data.status) {
        if (res.data.multiple) {
          // Multiple accounts on same phone — show selector
          setAccounts(res.data.accounts || []);
          setStep('select');
        } else {
          localStorage.setItem('memberToken', res.data.token);
          localStorage.setItem('memberInfo',  JSON.stringify(res.data.member));
          history.push('/user-portal');
        }
      } else {
        notification.error({ message: res.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'OTP verify नहीं हुआ।', placement: 'topRight' });
    } finally { setLoading(false); }
  };

  const selectAccount = async (userId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/member/select-account`, { phone, user_id: userId });
      if (res.data.status) {
        localStorage.setItem('memberToken', res.data.token);
        localStorage.setItem('memberInfo',  JSON.stringify(res.data.member));
        history.push('/user-portal');
      } else {
        notification.error({ message: res.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'कुछ समस्या हुई।', placement: 'topRight' });
    } finally { setLoading(false); }
  };

  // ── Registration handlers ─────────────────────────────────────────────────
  const regChange = (field, val) => setRegData(p => ({ ...p, [field]: val }));


  const validateForm = () => {
    if (!regData.name.trim())        { notification.warning({ message: 'नाम दर्ज करें', placement: 'topRight' }); return false; }
    if (!regData.father_name.trim()) { notification.warning({ message: 'पिता/पति का नाम दर्ज करें', placement: 'topRight' }); return false; }
    if (!regData.relation)           { notification.warning({ message: 'संबंध (Relation) चुनें', placement: 'topRight' }); return false; }
    if (!/^\d{10}$/.test(regData.phone) || !/^[6-9]/.test(regData.phone)) {
      notification.warning({ message: 'Valid 10-digit मोबाइल नंबर दर्ज करें', placement: 'topRight' }); return false;
    }
    if (!regData.village.trim())  { notification.warning({ message: 'गाँव का नाम दर्ज करें', placement: 'topRight' }); return false; }
    if (!regData.district.trim()) { notification.warning({ message: 'जिला दर्ज करें', placement: 'topRight' }); return false; }
    if (!joinMission21k && (!regData.announced_amount || parseInt(regData.announced_amount) < 100)) {
      notification.warning({ message: 'मासिक राशि कम से कम ₹100 दर्ज करें', placement: 'topRight' }); return false;
    }
    return true;
  };

  const proceedToPayment = async () => {
    if (!validateForm()) return;
    setRegLoading(true);
    try {
      // Mission 21K: force ₹200 if opted in
      const finalAmount = joinMission21k ? 200 : regData.announced_amount;
      const finalData   = { ...regData, announced_amount: finalAmount, campaign: joinMission21k ? 'mission_21k' : undefined, ref_member_id: selectedParent?.id || undefined };
      const check = await axios.post(`${baseUrl}/member/register/create-order`, { ...finalData, months: joinMission21k ? 1 : regMonths });
      if (!check.data.status) {
        notification.error({ message: check.data.message, placement: 'topRight' });
        return;
      }
      // Order created — open Razorpay
      await openRazorpay(check.data);
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'कुछ समस्या हुई, दोबारा try करें', placement: 'topRight' });
    } finally { setRegLoading(false); }
  };

  const openRazorpay = async (orderData) => {
    const ok = await loadRazorpay();
    if (!ok) { notification.error({ message: 'Payment gateway load नहीं हुआ। Reload करें।', placement: 'topRight' }); return; }

    setRegStep('paying');
    const rzp = new window.Razorpay({
      key:         rozerpay_key_id,
      order_id:    orderData.order_id,
      amount:      orderData.amount * 100,
      currency:    'INR',
      name:        'ABRSVS',
      description: joinMission21k ? 'Mission 21K — ₹200/माह सदस्यता' : `सदस्यता - ${orderData.months} माह`,
      prefill:     { contact: regData.phone, name: regData.name },
      theme:       { color: M },
      // redirect: true ensures UPI apps return to browser after payment
      redirect:    true,
      callback_url: `${window.location.origin}/user-portal-login?reg_verify=1`,
      handler: async (response) => {
        try {
          const res = await axios.post(`${baseUrl}/member/register/verify-payment`, {
            order_id:         orderData.order_id,
            payment_id:       response.razorpay_payment_id,
            signature:        response.razorpay_signature,
            name:             regData.name,
            father_name:      regData.father_name,
            relation:         regData.relation,
            relation_hn:      regData.relation,
            relation_en:      PORTAL_RELATION_MAP[regData.relation] || regData.relation,
            phone:            regData.phone,
            village:          regData.village,
            district:         regData.district,
            announced_amount: joinMission21k ? 200 : regData.announced_amount,
            months:           orderData.months,
            campaign:         joinMission21k ? 'mission_21k' : undefined,
            ref_member_id:    selectedParent?.id || undefined,
          });
          if (res.data.status) {
            localStorage.setItem('memberToken', res.data.token);
            localStorage.setItem('memberInfo',  JSON.stringify(res.data.member));
            setRegStep('success');
            notification.success({ message: 'पंजीकरण सफल! स्वागत है।', placement: 'topRight', duration: 3 });
            setTimeout(() => history.push('/user-portal'), 2000);
          } else {
            notification.error({ message: res.data.message, placement: 'topRight' });
            setRegStep('form');
          }
        } catch (err) {
          notification.error({ message: err.response?.data?.message || 'Payment verify नहीं हुई', placement: 'topRight' });
          setRegStep('form');
        }
      },
      modal: { ondismiss: () => { if (regStep === 'paying') setRegStep('form'); } },
    });
    rzp.open();
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  const inputStyle = { borderRadius: 8 };

  return (
    <div style={{
      minHeight: '100vh', background: '#f4f6fa',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, fontFamily: "'Segoe UI', 'Noto Sans Devanagari', sans-serif",
    }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 18, boxShadow: '0 12px 48px rgba(0,0,0,0.18)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, padding: '28px 24px 20px', textAlign: 'center' }}>
          <img src="/Shri_Kheteshwar.jpg" alt="श्री खेतेश्वर महाराज"
            style={{ width: 68, height: 68, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top center',
              border: `3px solid ${G}`, boxShadow: `0 0 0 3px rgba(255,215,0,0.2)`, marginBottom: 10 }} />
          <div style={{ fontSize: 15, fontWeight: 800, color: G, fontFamily: 'serif' }}>
            अखिल भारतीय राजपुरोहित समाज विकास संस्थान
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: G, fontFamily: 'serif' }}>
            श्री खेतेश्वर ब्रह्मधामतीर्थ आसोतरा
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>सदस्य पोर्टल</div>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
          {[
            { key: 'login',    label: 'Login',       icon: <LoginOutlined /> },
            { key: 'register', label: 'नया सदस्य बनें', icon: <UserAddOutlined /> },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setRegStep('form'); }}
              style={{
                flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                background: tab === t.key ? '#fff' : '#f9fafb',
                color:      tab === t.key ? M : '#6b7280',
                borderBottom: tab === t.key ? `2px solid ${M}` : '2px solid transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '24px 24px 28px' }}>

          {/* ── LOGIN TAB ── */}
          {tab === 'login' && (
            <>
              {step === 'select' ? (
            <>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                इस नंबर पर <strong style={{ color: '#111827' }}>{accounts.length} खाते</strong> मिले
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>
                +91 {phone} — कौन सा खाता खोलना है?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {accounts.map(acc => (
                  <button key={acc.id} onClick={() => selectAccount(acc.id)} disabled={loading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                      border: `1.5px solid #e5e7eb`, background: '#fafafa',
                      textAlign: 'left', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = M; e.currentTarget.style.background = `${M}08`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fafafa'; }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: acc.photo ? 'transparent' : `${M}15`,
                      border: `2px solid ${M}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {acc.photo
                        ? <img src={acc.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 18, fontWeight: 700, color: M }}>{(acc.name || '?')[0]}</span>
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{acc.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{acc.relation || 'सदस्य'}</div>
                    </div>
                    <div style={{ fontSize: 18, color: '#d1d5db' }}>›</div>
                  </button>
                ))}
              </div>
              <button onClick={() => { setStep('phone'); setOtp(''); setAccounts([]); }}
                style={{ background: 'none', border: 'none', color: M, fontSize: 12, cursor: 'pointer', marginTop: 14, width: '100%' }}>
                ← वापस जाएं
              </button>
            </>
          ) : step === 'phone' ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>पंजीकृत मोबाइल नंबर दर्ज करें</div>
                  <Input
                    prefix={<PhoneOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onPressEnter={sendOtp}
                    size="large" style={{ ...inputStyle, marginBottom: 16 }}
                    maxLength={10} inputMode="numeric"
                  />
                  <Button block size="large" loading={loading} onClick={sendOtp}
                    style={{ borderRadius: 10, fontWeight: 700, height: 48, background: '#f8f8f8', borderColor: '#e0e0e0', color: '#1a1a1a', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <WhatsAppOutlined style={{ fontSize: 18, color: '#25D366' }} />
                    Send OTP &nbsp;<span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>(WhatsApp)</span>
                  </Button>
                  <div style={{ marginTop: 14, padding: '10px 14px', background: '#fdf5e6', borderRadius: 8, border: '1px solid #f5e0b0', fontSize: 12, color: '#6b3a1f', lineHeight: 1.7 }}>
                    समाज शिक्षा निधि समर्पण योजना में पंजीकृत सदस्य एवं सामुदायिक व्यवसाय निर्देशिका (Business Directory) में पंजीकृत सदस्य login कर सकते हैं।
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                    OTP भेजा गया: <strong style={{ color: '#111827' }}>+91 {phone}</strong>
                  </div>
                  <button onClick={() => { setStep('phone'); setOtp(''); }}
                    style={{ background: 'none', border: 'none', color: M, fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
                    ← नंबर बदलें
                  </button>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>6-digit OTP दर्ज करें</div>
                  <Input
                    prefix={<SafetyOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="_ _ _ _ _ _"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onPressEnter={verifyOtp}
                    size="large"
                    style={{ ...inputStyle, letterSpacing: 6, textAlign: 'center', fontSize: 18, fontWeight: 700, marginBottom: 16 }}
                    maxLength={6} inputMode="numeric"
                  />
                  <Button block size="large" loading={loading} onClick={verifyOtp}
                    style={{ borderRadius: 10, fontWeight: 700, height: 46, background: M, borderColor: M, color: G }}>
                    Verify &amp; Login
                  </Button>
                  <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <button onClick={() => { setOtp(''); sendOtp(); }} disabled={loading}
                      style={{ background: 'none', border: 'none', color: M, fontSize: 12, cursor: 'pointer' }}>
                      OTP दोबारा भेजें
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── REGISTER TAB ── */}
          {tab === 'register' && (
            <>
              {regStep === 'success' ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: M, marginBottom: 6 }}>पंजीकरण सफल!</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Portal में redirect हो रहे हैं…</div>
                </div>
              ) : (
                <>
                  {/* Scheme banner */}
                  <div style={{
                    background: `linear-gradient(135deg, ${M}0f, ${G}18)`,
                    border: `1px solid ${M}30`,
                    borderRadius: 10, padding: '10px 14px', marginBottom: 16, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: M, fontFamily: 'serif' }}>
                      समाज शिक्षा निधि समर्पण योजना
                    </div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                      योजना में पंजीकरण के लिए नीचे फॉर्म भरें
                    </div>
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 700, color: M, marginBottom: 14 }}>नया सदस्य पंजीकरण</div>

                  {/* Form fields */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>नाम *</div>
                      <Input placeholder="पूरा नाम" value={regData.name}
                        onChange={e => regChange('name', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>पिता/पति का नाम *</div>
                      <Input placeholder="पिता/पति का नाम" value={regData.father_name}
                        onChange={e => regChange('father_name', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>संबंध (Relation) *</div>
                      <Select
                        placeholder="संबंध चुनें"
                        value={regData.relation || undefined}
                        onChange={v => regChange('relation', v)}
                        style={{ width: '100%' }}
                        options={[
                          { value: 'सुपुत्र श्री',    label: 'सुपुत्र श्री (Son of Mr.)' },
                          { value: 'सुपुत्री श्री',   label: 'सुपुत्री श्री (Daughter of Mr.)' },
                          { value: 'पत्नी श्री',      label: 'पत्नी श्री (Wife of Mr.)' },
                        ]}
                      />
                    </div>

                    {/* Parent member selection */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        onClick={() => { setHasParentMember(p => !p); setSelectedParent(null); setParentSearch(''); setParentResults([]); }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${hasParentMember ? M : '#d1d5db'}`, background: hasParentMember ? M : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {hasParentMember && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>क्या परिवार में कोई पहले से ABRSVS सदस्य है?</span>
                      </div>
                      {hasParentMember && (
                        <div style={{ marginTop: 10 }}>
                          {selectedParent ? (
                            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>{selectedParent.name}</div>
                                {selectedParent.village && <div style={{ fontSize: 10, color: '#6b7280' }}>📍 {selectedParent.village} {selectedParent.membership_no ? `• #${selectedParent.membership_no}` : ''}</div>}
                              </div>
                              <button onClick={() => { setSelectedParent(null); setParentSearch(''); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16 }}>×</button>
                            </div>
                          ) : (
                            <>
                              <Input
                                placeholder="नाम या membership number से खोजें..."
                                value={parentSearch}
                                onChange={async e => {
                                  const v = e.target.value;
                                  setParentSearch(v);
                                  if (v.length >= 2) {
                                    try {
                                      const r = await axios.get(`${baseUrl}/member/search-for-registration?q=${encodeURIComponent(v)}`);
                                      setParentResults(r.data.result || []);
                                    } catch { setParentResults([]); }
                                  } else setParentResults([]);
                                }}
                                style={{ ...inputStyle, marginBottom: 6 }}
                              />
                              {parentResults.map(m => (
                                <div key={m.id} onClick={() => { setSelectedParent(m); setParentResults([]); }}
                                  style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: '#fff', border: '1px solid #e5e7eb', marginBottom: 4 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{m.name}</div>
                                  <div style={{ fontSize: 10, color: '#9ca3af' }}>📍 {m.village || '—'} {m.membership_no ? `• #${m.membership_no}` : ''}</div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>मोबाइल नंबर *</div>
                      <Input prefix={<PhoneOutlined style={{ color: '#9ca3af' }} />}
                        placeholder="10-digit mobile number" value={regData.phone}
                        onChange={e => regChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        maxLength={10} inputMode="numeric" style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>गाँव *</div>
                        <Input placeholder="गाँव" value={regData.village}
                          onChange={e => regChange('village', e.target.value)} style={inputStyle} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>जिला *</div>
                        <Input placeholder="जिला" value={regData.district}
                          onChange={e => regChange('district', e.target.value)} style={inputStyle} />
                      </div>
                    </div>
                    {/* Mission 21K toggle */}
                    {mission21kEnabled && <div
                      onClick={() => { setJoinMission21k(p => !p); if (!joinMission21k) regChange('announced_amount', 200); }}
                      style={{
                        cursor: 'pointer', borderRadius: 10, padding: '12px 14px',
                        background: joinMission21k ? 'linear-gradient(135deg,#92400e,#b45309)' : '#fef3c7',
                        border: `2px solid ${joinMission21k ? '#fbbf24' : '#fde68a'}`,
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 20 }}>🎯</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: joinMission21k ? '#fde68a' : '#92400e' }}>
                          Mission 21K में जुड़ें — ₹200/माह
                        </div>
                        <div style={{ fontSize: 10, color: joinMission21k ? 'rgba(255,255,255,0.75)' : '#b45309' }}>
                          {joinMission21k ? '✓ Mission 21K selected — ₹200 fixed' : 'ABRSVS का सामूहिक सदस्यता अभियान'}
                        </div>
                      </div>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: joinMission21k ? '#fde68a' : '#fff',
                        border: `2px solid ${joinMission21k ? '#fbbf24' : '#d1d5db'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 900, color: '#92400e',
                        flexShrink: 0,
                      }}>
                        {joinMission21k ? '✓' : ''}
                      </div>
                    </div>}

                    {!joinMission21k && (
                      <>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>मासिक राशि (₹) *</div>
                          <InputNumber
                            placeholder="जैसे: 500" min={100} style={{ width: '100%', borderRadius: 8 }}
                            value={regData.announced_amount || undefined}
                            onChange={v => regChange('announced_amount', v)}
                            formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={v => v.replace(/₹\s?|(,*)/g, '')}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>भुगतान अवधि</div>
                          <Select
                            value={regMonths} onChange={v => setRegMonths(v)}
                            style={{ width: '100%' }} size="large"
                            options={[
                              { value: 1,  label: `मासिक — 1 माह${(parseInt(regData.announced_amount)||0) > 0 ? '  (₹'+((parseInt(regData.announced_amount)||0)*1).toLocaleString()+')':''}` },
                              { value: 3,  label: `त्रैमासिक — 3 माह${(parseInt(regData.announced_amount)||0) > 0 ? '  (₹'+((parseInt(regData.announced_amount)||0)*3).toLocaleString()+')':''}` },
                              { value: 6,  label: `छमाही — 6 माह${(parseInt(regData.announced_amount)||0) > 0 ? '  (₹'+((parseInt(regData.announced_amount)||0)*6).toLocaleString()+')':''}` },
                              { value: 12, label: `वार्षिक — 12 माह${(parseInt(regData.announced_amount)||0) > 0 ? '  (₹'+((parseInt(regData.announced_amount)||0)*12).toLocaleString()+')':''}` },
                            ]}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Pay button */}
                  <Button block size="large" loading={regLoading} onClick={proceedToPayment}
                    style={{ marginTop: 18, borderRadius: 10, fontWeight: 700, height: 48, background: M, borderColor: M, color: G }}>
                    {regLoading ? 'Processing…' : joinMission21k
                      ? 'Pay ₹200 — Mission 21K Join करें'
                      : `Pay & Join — ₹${((parseInt(regData.announced_amount)||0) * regMonths).toLocaleString()}`
                    }
                  </Button>

                  <div style={{ marginTop: 12, padding: '10px 14px', background: '#fdf5e6', borderRadius: 8, border: '1px solid #f5e0b0', fontSize: 11, color: '#6b3a1f', lineHeight: 1.7 }}>
                    भुगतान के बाद आपका पंजीकरण तुरंत सक्रिय हो जाएगा और आप Login कर सकेंगे।
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'center', background: '#fafafa' }}>
          <div style={{ fontSize: 11, color: '#9ca3af', letterSpacing: 0.3 }}>
            श्री खेतेश्वर ब्रह्मधामतीर्थ आसोतरा
          </div>
        </div>

      </div>
    </div>
  );
}
